import type { useAuth } from './authContext';

export type OnboardingPhase = 'tos' | 'add-asset' | 'done';

/**
 * SIL-1305 — single source of truth for "where is this individual-org user
 * in the first-run wizard": `TosGate`/`AddAssetGate`/`IndividualProfileGate`
 * (`routes/__root.tsx`) derive their own redirect predicates from this
 * instead of re-deriving the same condition three times (the same
 * two-independent-conditions hazard PR #1691 fixed for TosGate/
 * IndividualProfileGate — see `individualProfileGateWouldRedirect`'s
 * docstring there). `AppShell`'s setup rail, `NavCommandPalette`,
 * `AddAssetSuccessPanel`'s `firstRun` mode, and `/individual-profile`'s
 * profile banner all read this same phase rather than re-checking
 * canSell/canBuy/hasAssets independently.
 *
 * Rules, in order — a business org (`organizationType !== 'individual'`) is
 * always `'done'`; the business-org variant of this wizard is explicitly out
 * of scope (deferred to a follow-up issue).
 */
export function onboardingPhase(
  auth: Pick<
    ReturnType<typeof useAuth>,
    'tosAccepted' | 'organizationType' | 'canSell' | 'canBuy' | 'hasAssets'
  >,
): OnboardingPhase {
  if (!auth.tosAccepted) return 'tos';
  if (auth.organizationType !== 'individual') return 'done';
  if (auth.canSell && auth.canBuy) return 'done';
  if (!auth.hasAssets) return 'add-asset';
  // SIL-1812: profile completion is deferred to the Exchange pages
  // (`individualProfileGateWouldRedirect`), so an individual with assets is done.
  return 'done';
}
